import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { 
  Calculator, 
  Activity, 
  Target, 
  Users, 
  TrendingUp,
  CheckCircle,
  AlertTriangle,
  Info
} from 'lucide-react';

export const BMIEducationSection = () => {
  return (
    <section id="bmi-education" className="py-16 bg-muted/30">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* What is BMI Section */}
        <article id="what-is-bmi" className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Info className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">What is BMI?</h2>
          </div>
          
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              <strong>Body Mass Index (BMI)</strong> is a numerical value calculated from your height and weight that serves as a screening tool to categorize individuals into different weight categories. Developed by Belgian mathematician Adolphe Quetelet in the 1830s, BMI has become one of the most widely used metrics in healthcare for assessing whether a person has a healthy body weight relative to their height.
            </p>
            
            <p>
              The BMI measurement provides a quick and inexpensive method to screen for weight categories that may lead to health problems. Healthcare professionals, fitness experts, and public health organizations worldwide use BMI as an initial assessment tool because it requires only two simple measurements: your height and your weight. This simplicity has made it the standard screening tool in clinical settings, workplace health assessments, and population health studies.
            </p>
            
            <p>
              BMI categorizes adults into four primary classifications according to the World Health Organization (WHO) standards:
            </p>
            
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Underweight:</strong> BMI less than 18.5 — May indicate malnutrition, eating disorders, or other health conditions that require medical attention.</li>
              <li><strong>Normal weight:</strong> BMI between 18.5 and 24.9 — Associated with the lowest health risks and optimal body composition for most individuals.</li>
              <li><strong>Overweight:</strong> BMI between 25 and 29.9 — Indicates elevated health risks and may warrant lifestyle modifications.</li>
              <li><strong>Obese:</strong> BMI of 30 or greater — Significantly increases the risk of various health conditions including heart disease, type 2 diabetes, and certain cancers.</li>
            </ul>
            
            <p>
              Understanding your BMI can be the first step toward making informed decisions about your health. While BMI alone doesn't diagnose health conditions, it serves as a valuable starting point for conversations with healthcare providers about weight management, nutrition, and overall wellness strategies.
            </p>
            
            <p>
              It's important to recognize that BMI is a population-level metric originally designed for epidemiological studies. When applied to individuals, additional factors such as muscle mass, bone density, age, sex, and ethnic background should be considered for a complete health assessment. This is why tools like our <Link to="/#calculator" className="text-primary hover:underline">BMI Calculator</Link> offer additional metrics like waist-to-height ratio and athlete-specific interpretations.
            </p>
            
            <p>
              Research consistently shows that maintaining a BMI within the normal range is associated with lower risks of chronic diseases, improved longevity, and better quality of life. However, the relationship between BMI and health isn't always linear—some individuals with "overweight" BMIs may be metabolically healthy, while others with "normal" BMIs may have elevated health risks due to other factors like visceral fat distribution or sedentary lifestyle.
            </p>
          </div>
        </article>

        {/* How BMI is Calculated Section */}
        <article id="how-bmi-calculated" className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">How BMI is Calculated</h2>
          </div>
          
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              BMI is calculated using a simple mathematical formula that relates your weight to your height. The calculation can be performed using either metric or imperial (US) units, making it accessible regardless of which measurement system you prefer.
            </p>
            
            {/* Formula Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-8">
              <Card className="bg-card border-primary/20">
                <CardContent className="p-6">
                  <h4 className="font-bold text-foreground mb-3">Metric Formula</h4>
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <code className="text-lg font-mono text-primary">
                      BMI = weight (kg) ÷ height² (m²)
                    </code>
                  </div>
                  <p className="text-sm mt-3">
                    <strong>Example:</strong> A person weighing 70 kg and 1.75 m tall:<br/>
                    BMI = 70 ÷ (1.75 × 1.75) = 70 ÷ 3.0625 = <strong>22.9</strong>
                  </p>
                </CardContent>
              </Card>
              
              <Card className="bg-card border-primary/20">
                <CardContent className="p-6">
                  <h4 className="font-bold text-foreground mb-3">Imperial (US) Formula</h4>
                  <div className="bg-muted rounded-lg p-4 text-center">
                    <code className="text-lg font-mono text-primary">
                      BMI = (weight (lb) × 703) ÷ height² (in²)
                    </code>
                  </div>
                  <p className="text-sm mt-3">
                    <strong>Example:</strong> A person weighing 154 lb and 69 inches tall:<br/>
                    BMI = (154 × 703) ÷ (69 × 69) = 108,262 ÷ 4,761 = <strong>22.7</strong>
                  </p>
                </CardContent>
              </Card>
            </div>
            
            <p>
              The constant 703 in the imperial formula is a conversion factor that accounts for the difference between metric and imperial units. Both formulas will yield the same BMI result when equivalent measurements are used.
            </p>
            
            <p>
              Our <Link to="/#calculator" className="text-primary hover:underline">BMI Calculator</Link> automatically handles these calculations for you, supporting both metric (kg/cm) and US (lb/ft-in) units. Simply enter your measurements, and you'll receive instant, accurate results along with additional health metrics like BMI Prime and Ponderal Index.
            </p>
          </div>
        </article>

        {/* Is BMI Accurate Section */}
        <article id="is-bmi-accurate" className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Target className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">Is BMI Accurate?</h2>
          </div>
          
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              BMI accuracy is one of the most debated topics in health and fitness. While BMI remains a valuable screening tool, it has several important limitations that everyone should understand before drawing conclusions about their health based solely on this metric.
            </p>
            
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-4">Strengths of BMI</h3>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <p className="m-0"><strong>Simplicity:</strong> Requires only height and weight measurements, making it accessible and inexpensive.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <p className="m-0"><strong>Population-level accuracy:</strong> Highly effective for assessing health trends across large groups.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <p className="m-0"><strong>Correlation with health risks:</strong> At extreme values, BMI strongly correlates with obesity-related diseases.</p>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <p className="m-0"><strong>Standardization:</strong> Provides a universal language for discussing weight status globally.</p>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-4">Limitations of BMI</h3>
            
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                <p className="m-0"><strong>Doesn't distinguish fat from muscle:</strong> A muscular athlete may have a high BMI despite having low body fat.</p>
              </div>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                <p className="m-0"><strong>Ignores fat distribution:</strong> Visceral fat (around organs) is more dangerous than subcutaneous fat, but BMI can't differentiate.</p>
              </div>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                <p className="m-0"><strong>Age and gender blind:</strong> Doesn't account for natural body composition changes with age or between sexes.</p>
              </div>
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 mt-1 flex-shrink-0" />
                <p className="m-0"><strong>Ethnic variations:</strong> Health risks may occur at different BMI levels for different ethnic groups.</p>
              </div>
            </div>
            
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-4">Making BMI More Accurate</h3>
            
            <p>
              For a more complete health picture, combine BMI with other measurements:
            </p>
            
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Waist-to-Height Ratio:</strong> A ratio below 0.5 indicates healthy central fat levels—often a better predictor of cardiovascular risk than BMI.</li>
              <li><strong>Body Fat Percentage:</strong> Direct measurement of fat mass provides more accurate body composition data.</li>
              <li><strong>Waist Circumference:</strong> Elevated waist measurements (&gt;40" for men, &gt;35" for women) indicate higher health risks regardless of BMI.</li>
            </ul>
            
            <p>
              Our calculator includes <strong>Waist-to-Height Ratio</strong> calculations and <strong>Athlete Mode</strong> to provide context-aware interpretations that address many of BMI's limitations.
            </p>
          </div>
        </article>

        {/* BMI for Athletes Section */}
        <article id="bmi-for-athletes" className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Activity className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">BMI for Athletes</h2>
          </div>
          
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              <strong>Athletes face a unique challenge with BMI</strong>—the standard categories often misclassify fit, muscular individuals as "overweight" or even "obese." This happens because BMI cannot distinguish between muscle mass and fat mass, and athletes typically carry significantly more muscle than the average person.
            </p>
            
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-4">Why Standard BMI Fails Athletes</h3>
            
            <p>
              Consider a professional rugby player who is 6'0" (183 cm) tall and weighs 220 lbs (100 kg). Their BMI calculates to 29.9—just under the "obese" threshold. Yet this athlete may have only 12% body fat, exceptional cardiovascular fitness, and excellent metabolic health markers. The BMI simply cannot capture this reality.
            </p>
            
            <p>
              Research published in the <em>International Journal of Obesity</em> found that using BMI alone misclassified approximately 50% of women and 25% of men who had healthy body fat percentages as "overweight." Among athletes, these misclassification rates are even higher.
            </p>
            
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-4">Athlete BMI Categories</h3>
            
            <p>
              For trained athletes, sports medicine professionals often use adjusted BMI interpretations:
            </p>
            
            <Card className="bg-card my-6">
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <h4 className="font-bold text-foreground">Endurance Athletes</h4>
                    <p className="text-sm">Runners, cyclists, swimmers typically thrive with BMIs of 18.5-23. Lower body weight improves efficiency in weight-bearing activities.</p>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-bold text-foreground">Power Athletes</h4>
                    <p className="text-sm">Weightlifters, football players, wrestlers often have healthy BMIs of 25-30+ due to muscle mass requirements for their sports.</p>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-bold text-foreground">Mixed Sport Athletes</h4>
                    <p className="text-sm">Soccer, basketball, tennis players typically fall in the 22-27 range, balancing speed with strength requirements.</p>
                  </div>
                  <div className="space-y-3">
                    <h4 className="font-bold text-foreground">Combat Sport Athletes</h4>
                    <p className="text-sm">Boxers, MMA fighters, martial artists vary widely (20-28) depending on weight class and fighting style.</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-4">What Athletes Should Track Instead</h3>
            
            <p>
              While BMI provides a baseline reference, athletes benefit more from tracking:
            </p>
            
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Body Fat Percentage:</strong> Male athletes typically range 6-17%, females 14-24% depending on sport.</li>
              <li><strong>Lean Body Mass:</strong> Tracking muscle gains independent of fat changes.</li>
              <li><strong>Performance Metrics:</strong> Speed, strength, endurance improvements matter more than scale weight.</li>
              <li><strong>Waist-to-Height Ratio:</strong> Remains valid for athletes and indicates visceral fat levels.</li>
            </ul>
            
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mt-8">
              <h4 className="font-bold text-foreground mb-2 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                Try Our Athlete Mode
              </h4>
              <p className="m-0">
                Our <Link to="/#calculator" className="text-primary hover:underline font-medium">BMI Calculator's Athlete Mode</Link> provides sport-specific interpretations, lean mass bias warnings, running pace impact estimates, and hydration recommendations tailored for active individuals. Enable it by toggling "Athlete Mode" in the calculator above.
              </p>
            </div>
          </div>
        </article>

        {/* Healthy BMI Chart Section */}
        <article id="healthy-bmi-chart" className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground">Healthy BMI Chart</h2>
          </div>
          
          <div className="prose prose-lg max-w-none text-muted-foreground space-y-4">
            <p>
              The following chart shows BMI categories according to the World Health Organization (WHO) standards. Use this as a quick reference to understand where your BMI falls and what it may indicate about your health.
            </p>
            
            {/* BMI Categories Table */}
            <Card className="bg-card my-6 overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-muted">
                        <th className="text-left py-4 px-6 font-bold text-foreground">BMI Range</th>
                        <th className="text-left py-4 px-6 font-bold text-foreground">Category</th>
                        <th className="text-left py-4 px-6 font-bold text-foreground">Health Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-border">
                        <td className="py-4 px-6 font-mono">Below 18.5</td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                            Underweight
                          </span>
                        </td>
                        <td className="py-4 px-6">Increased risk of nutritional deficiencies, osteoporosis</td>
                      </tr>
                      <tr className="border-t border-border bg-green-50/50 dark:bg-green-900/10">
                        <td className="py-4 px-6 font-mono font-bold text-green-700 dark:text-green-400">18.5 – 24.9</td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                            Normal Weight ✓
                          </span>
                        </td>
                        <td className="py-4 px-6 font-medium text-green-700 dark:text-green-400">Lowest health risk — optimal range</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-4 px-6 font-mono">25.0 – 29.9</td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                            Overweight
                          </span>
                        </td>
                        <td className="py-4 px-6">Moderate risk of heart disease, type 2 diabetes</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-4 px-6 font-mono">30.0 – 34.9</td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                            Obese Class I
                          </span>
                        </td>
                        <td className="py-4 px-6">High risk of cardiovascular disease, metabolic syndrome</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-4 px-6 font-mono">35.0 – 39.9</td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                            Obese Class II
                          </span>
                        </td>
                        <td className="py-4 px-6">Very high risk of serious health conditions</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-4 px-6 font-mono">40.0 and above</td>
                        <td className="py-4 px-6">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-200 text-red-900 dark:bg-red-900/50 dark:text-red-200">
                            Obese Class III
                          </span>
                        </td>
                        <td className="py-4 px-6">Extremely high risk — medical intervention recommended</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            {/* Height/Weight Reference Table */}
            <h3 className="text-xl font-semibold text-foreground mt-8 mb-4">Healthy Weight Ranges by Height</h3>
            
            <p>
              This table shows the approximate healthy weight range (BMI 18.5-24.9) for different heights:
            </p>
            
            <Card className="bg-card my-6 overflow-hidden">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted">
                        <th className="text-left py-3 px-4 font-bold text-foreground">Height</th>
                        <th className="text-left py-3 px-4 font-bold text-foreground">Healthy Weight Range</th>
                        <th className="text-left py-3 px-4 font-bold text-foreground">Height</th>
                        <th className="text-left py-3 px-4 font-bold text-foreground">Healthy Weight Range</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-t border-border">
                        <td className="py-3 px-4">5'0" (152 cm)</td>
                        <td className="py-3 px-4">95–128 lbs (43–58 kg)</td>
                        <td className="py-3 px-4">5'7" (170 cm)</td>
                        <td className="py-3 px-4">121–163 lbs (55–74 kg)</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-3 px-4">5'1" (155 cm)</td>
                        <td className="py-3 px-4">98–132 lbs (44–60 kg)</td>
                        <td className="py-3 px-4">5'8" (173 cm)</td>
                        <td className="py-3 px-4">125–168 lbs (57–76 kg)</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-3 px-4">5'2" (157 cm)</td>
                        <td className="py-3 px-4">101–136 lbs (46–62 kg)</td>
                        <td className="py-3 px-4">5'9" (175 cm)</td>
                        <td className="py-3 px-4">128–173 lbs (58–78 kg)</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-3 px-4">5'3" (160 cm)</td>
                        <td className="py-3 px-4">104–141 lbs (47–64 kg)</td>
                        <td className="py-3 px-4">5'10" (178 cm)</td>
                        <td className="py-3 px-4">132–178 lbs (60–81 kg)</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-3 px-4">5'4" (163 cm)</td>
                        <td className="py-3 px-4">108–145 lbs (49–66 kg)</td>
                        <td className="py-3 px-4">5'11" (180 cm)</td>
                        <td className="py-3 px-4">136–183 lbs (62–83 kg)</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-3 px-4">5'5" (165 cm)</td>
                        <td className="py-3 px-4">111–150 lbs (50–68 kg)</td>
                        <td className="py-3 px-4">6'0" (183 cm)</td>
                        <td className="py-3 px-4">140–189 lbs (64–86 kg)</td>
                      </tr>
                      <tr className="border-t border-border">
                        <td className="py-3 px-4">5'6" (168 cm)</td>
                        <td className="py-3 px-4">115–154 lbs (52–70 kg)</td>
                        <td className="py-3 px-4">6'1" (185 cm)</td>
                        <td className="py-3 px-4">144–194 lbs (65–88 kg)</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            
            <div className="bg-muted/50 rounded-xl p-6 mt-8">
              <p className="text-sm m-0">
                <strong>Important:</strong> These ranges are general guidelines. Individual health depends on many factors beyond BMI, including muscle mass, bone density, age, sex, and overall fitness level. Always consult with a healthcare provider for personalized health advice. Use our <Link to="/#calculator" className="text-primary hover:underline">BMI Calculator</Link> above for instant, personalized results.
              </p>
            </div>
          </div>
        </article>
        
      </div>
    </section>
  );
};

export default BMIEducationSection;
